/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  PlusCircle,
  Globe,
  ChevronDown,
  Check
} from 'lucide-react';
import { accountService } from '../services/accountService';
import toastService from '../utils/toastService';

const NewAccount = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    status: 'active'
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) { errors.name = 'Account name is required'; }
    if (!form.slug.trim()) { errors.slug = 'Slug is required'; }
    else if (!/^[a-z0-9-]+$/.test(form.slug)) { errors.slug = 'Slug must only contain lowercase letters, numbers, and hyphens'; }
    else { 
      // No error
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Auto-generate slug from name
  const handleNameChange = (e) => {
    const name = e.target.value;
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setForm(f => ({ ...f, name, slug }));
    if (fieldErrors.name) setFieldErrors(prev => ({ ...prev, name: null }));
    if (fieldErrors.slug) setFieldErrors(prev => ({ ...prev, slug: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }
    try {
      setSubmitting(true);
      await accountService.createAccount({
        name: form.name.trim(),
        slug: form.slug.trim(),
        status: form.status
      });
      toastService.success('Account created successfully');
      navigate('/accounts');
    } catch (err) {
      const msg = err.response?.data?.message;
      const errorMsg = Array.isArray(msg) ? msg[0] : (msg || 'Failed to create account');
      toastService.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden rounded-2xl border border-base-300 shadow-sm bg-base-100">
      <div className="flex-1 flex flex-col overflow-y-auto bg-base-100/50 backdrop-blur-md">

        {/* Header Section */}
        <div className="p-6 sm:p-8 border-b border-base-300 flex-shrink-0 flex justify-between items-start">
          <div>
            <h1 className="page-title mb-1">Create Account</h1>
          </div>
          <button onClick={() => navigate('/accounts')} className="p-2 hover:bg-base-200 rounded-xl transition-colors text-base-content/40 hover:text-base-content">
            <X size={24} />
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6 sm:p-8 max-w-[1200px]">
          <form onSubmit={handleSubmit} className="space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

              {/* Account Name */}
              <div className="space-y-2">
                <label htmlFor="name" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                  Account Name *
                </label>
                <input
                  id="name"
                  type="text"
                  maxLength={50}
                  placeholder="e.g. Acme Corporation"
                  className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors.name ? 'border-error' : 'border-base-300'}`}
                  value={form.name}
                  onChange={handleNameChange}
                />
                {fieldErrors.name && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.name}</p>}
              </div>

              {/* Slug — auto-generated, but editable */}
              <div className="space-y-2">
                <label htmlFor="slug" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                  Slug *
                </label>
                <div className="relative">
                  <input
                    id="slug"
                    type="text"
                    maxLength={50}
                    placeholder="acme-corporation"
                    className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors.slug ? 'border-error' : 'border-base-300'}`}
                    value={form.slug}
                    onChange={e => {
                      const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/--+/g, '-');
                      setForm(f => ({ ...f, slug }));
                      if (fieldErrors.slug) setFieldErrors(prev => ({ ...prev, slug: null }));
                    }}
                  />
                </div>
                {fieldErrors.slug && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.slug}</p>}
              </div>

              {/* Status Dropdown */}
              {/* <div className="space-y-2">
                <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                  Status
                </label>
                <div className="relative" ref={statusDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsStatusOpen(!isStatusOpen)}
                    className="flex items-center justify-between gap-2 w-full rounded-xl px-4 py-3 bg-base-100 border border-base-300 text-base-content font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all group"
                  >
                    <span className="truncate flex-1 text-left uppercase text-[10px] tracking-wider">{form.status}</span>
                    <ChevronDown size={14} className={`text-base-content/20 group-hover:text-primary transition-transform ${isStatusOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isStatusOpen && (
                    <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col">
                      {['active', 'inactive'].map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => { 
                            setForm(f => ({ ...f, status: s })); 
                            setIsStatusOpen(false);
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 last:border-none ${form.status === s ? 'bg-primary/5' : ''}`}
                        >
                          <span className={`text-[11px] font-black uppercase tracking-widest ${form.status === s ? 'text-primary' : 'text-base-content/40 group-hover:text-primary'}`}>{s}</span>
                          {form.status === s && <Check size={12} className="text-primary ml-auto" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {fieldErrors.status && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.status}</p>}
              </div> */}

            </div>

            {/* Actions */}
            <div className="flex gap-4 pt-12 border-t border-base-content/5">
              <button
                type="button"
                onClick={() => navigate('/accounts')}
                className="flex-1 py-4 px-8 border border-base-content/20 rounded-xl font-black uppercase tracking-[0.2em] text-xs text-base-content/40 hover:bg-base-content/5 hover:text-base-content transition-all"
              >
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="flex-1 btn-premium">
                {submitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    <span>Creating...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <PlusCircle size={18} />
                    <span>Create Account</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewAccount;
